import { useState, useEffect, useCallback } from 'react';
import { planosApi, Plano } from '../api/api';

const TELAS_DISPONIVEIS = [
  { path: '/', label: 'Sessões WhatsApp' },
  { path: '/disparador', label: 'Disparador' },
  { path: '/bases', label: 'Importar Bases' },
  { path: '/templates', label: 'Modelos de Mensagens' },
  { path: '/leads-pendentes', label: 'Leads Pendentes' },
  { path: '/respostas', label: 'Quem Respondeu' },
  { path: '/minha-assinatura', label: 'Minha Assinatura' },
];

const EMPTY_FORM = {
  nome: '',
  valor: '',
  maxConexoes: '1',
  maxDisparos: '0',
  maxSessoesAtivas: '1',
  especificacoes: '',
  destaque: false,
  ordem: '0',
  telas: [] as string[],
  temMaturador: false,
};

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">{label}</label>
      {hint && <p className="text-xs text-gray-400 mb-1.5">{hint}</p>}
      {children}
    </div>
  );
}

function NumInput({ value, onChange, min = 0, placeholder = '0' }: { value: string; onChange: (v: string) => void; min?: number; placeholder?: string }) {
  return (
    <input
      type="number"
      min={min}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full h-10 px-3 rounded-xl border bg-gray-50 text-sm text-gray-900 focus:outline-none focus:border-indigo-400 focus: transition-colors"
    />
  );
}

function TextInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full h-10 px-3 rounded-xl border bg-gray-50 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-indigo-400 focus: transition-colors"
    />
  );
}

export default function PlanosAdminPage() {
  const [planos, setPlanos] = useState<Plano[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await planosApi.listAll();
      setPlanos(data);
    } catch {
      setError('Erro ao carregar planos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 2500);
  };

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditingId('new');
    setError('');
  };

  const openEdit = (p: Plano) => {
    setForm({
      nome: p.nome,
      valor: Number(p.valor).toFixed(2).replace('.', ','),
      maxConexoes: String(p.maxConexoes),
      maxDisparos: String(p.maxDisparos),
      maxSessoesAtivas: String(p.maxSessoesAtivas),
      especificacoes: p.especificacoes.join('\n'),
      destaque: p.destaque,
      ordem: String(p.ordem),
      telas: p.telas || [],
      temMaturador: !!(p as any).temMaturador,
    });
    setEditingId(p.id);
    setError('');
  };

  const cancelEdit = () => { setEditingId(null); setError(''); };

  const f = (field: keyof typeof EMPTY_FORM) => (v: string | boolean) =>
    setForm(prev => ({ ...prev, [field]: v }));

  const toggleTela = (path: string) =>
    setForm(prev => ({
      ...prev,
      telas: prev.telas.includes(path)
        ? prev.telas.filter(t => t !== path)
        : [...prev.telas, path],
    }));

  const handleSave = async () => {
    if (!form.nome.trim()) { setError('Nome do plano é obrigatório'); return; }
    const val = parseFloat(form.valor.replace(',', '.'));
    if (isNaN(val) || val <= 0) { setError('Informe um valor válido'); return; }
    const specs = form.especificacoes.split('\n').map(s => s.trim()).filter(Boolean);

    setSaving(true);
    setError('');
    try {
      const dto = {
        nome: form.nome.trim(),
        valor: val,
        maxConexoes: Math.max(0, parseInt(form.maxConexoes) || 0),
        maxDisparos: Math.max(0, parseInt(form.maxDisparos) || 0),
        maxSessoesAtivas: Math.max(0, parseInt(form.maxSessoesAtivas) || 0),
        especificacoes: specs,
        destaque: form.destaque,
        ordem: parseInt(form.ordem) || 0,
        telas: form.telas,
        temMaturador: form.temMaturador,
      };
      if (editingId === 'new') {
        await planosApi.create(dto);
        flash('Plano criado com sucesso!');
      } else {
        await planosApi.update(editingId as number, dto);
        flash('Plano atualizado!');
      }
      setEditingId(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Erro ao salvar plano.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (p: Plano) => {
    try {
      await planosApi.update(p.id, { ativo: !p.ativo });
      await load();
    } catch { setError('Erro ao atualizar status.'); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Excluir este plano permanentemente?')) return;
    try {
      await planosApi.remove(id);
      flash('Plano excluído.');
      await load();
    } catch { setError('Erro ao excluir plano.'); }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-black text-gray-900">Planos de Assinatura</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            Configure os planos exibidos na landing page e na tela de pagamento.
          </p>
        </div>
        {editingId === null && (
          <button
            onClick={openNew}
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Novo plano
          </button>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-2.5 border border-red-200 rounded-xl px-4 py-3 mb-4 text-sm bg-red-50 text-red-600">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2.5 border border-emerald-200 rounded-xl px-4 py-3 mb-4 text-sm bg-emerald-50 text-emerald-700">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {success}
        </div>
      )}

      {/* ── Formulário de edição ─────────────────────────────────────── */}
      {editingId !== null && (
        <div className="border rounded-2xl shadow-sm p-7 mb-6 bg-white border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-base font-bold text-gray-900">
              {editingId === 'new' ? 'Novo plano' : 'Editar plano'}
            </h2>
            <button onClick={cancelEdit} className="text-gray-400 hover:text-gray-600 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Nome */}
            <Field label="Nome do plano">
              <TextInput value={form.nome} onChange={f('nome')} placeholder="Ex: Intermediário" />
            </Field>

            {/* Valor */}
            <Field label="Preço mensal (R$)">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-semibold">R$</span>
                <input
                  type="text"
                  value={form.valor}
                  onChange={e => f('valor')(e.target.value.replace(/[^0-9,.]/, ''))}
                  placeholder="99,90"
                  className="w-full h-10 pl-9 pr-3 rounded-xl border bg-gray-50 text-sm text-gray-900 focus:outline-none focus:border-indigo-400 focus: transition-colors"
                />
              </div>
            </Field>

            {/* Max conexões */}
            <Field label="Máximo de conexões WhatsApp" hint="Quantos números o usuário pode conectar">
              <NumInput value={form.maxConexoes} onChange={f('maxConexoes')} min={1} placeholder="1" />
            </Field>

            {/* Max sessões ativas */}
            <Field label="Sessões simultâneas ativas" hint="Sessões rodando ao mesmo tempo">
              <NumInput value={form.maxSessoesAtivas} onChange={f('maxSessoesAtivas')} min={1} placeholder="1" />
            </Field>

            {/* Max disparos */}
            <Field label="Limite de disparos por dia" hint="0 = ilimitado">
              <NumInput value={form.maxDisparos} onChange={f('maxDisparos')} min={0} placeholder="0 = ilimitado" />
            </Field>

            {/* Ordem */}
            <Field label="Ordem de exibição" hint="Menor número aparece primeiro">
              <NumInput value={form.ordem} onChange={f('ordem')} min={0} placeholder="1" />
            </Field>

            {/* Especificações — full width */}
            <div className="md:col-span-2">
              <Field label="Recursos incluídos (um por linha)" hint="Estes itens aparecem como lista de checkmarks na tela de pagamento e na landing page">
                <textarea
                  value={form.especificacoes}
                  onChange={e => f('especificacoes')(e.target.value)}
                  rows={5}
                  placeholder={"3 sessões WhatsApp\nAté 2.000 disparos/dia\nTemplates avançados\nGestão de leads\nSuporte prioritário"}
                  className="w-full px-3 py-2.5 rounded-xl border bg-gray-50 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-indigo-400 focus: transition-colors resize-none font-mono leading-relaxed"
                />
              </Field>
            </div>

            {/* Destaque */}
            <div className="md:col-span-2">
              <label className="flex items-center gap-3 cursor-pointer group">
                <div
                  onClick={() => f('destaque')(!form.destaque)}
                  className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                    form.destaque ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 group-hover:border-indigo-400'
                  }`}
                >
                  {form.destaque && (
                    <svg className="w-3 h-3 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Destacar como "Mais popular"</p>
                  <p className="text-xs text-gray-400">Exibe badge e borda de destaque neste plano</p>
                </div>
              </label>
            </div>

            {/* Inclui Maturador de Chip */}
            <div className="md:col-span-2">
              <label className="flex items-center gap-3 cursor-pointer group">
                <div
                  onClick={() => f('temMaturador')(!form.temMaturador)}
                  className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all`}
                  style={{
                    background: form.temMaturador ? '#3B82F6' : 'transparent',
                    borderColor: form.temMaturador ? '#3B82F6' : '#D1D5DB',
                  }}
                >
                  {form.temMaturador && (
                    <svg className="w-3 h-3" fill="none" stroke="#FFFFFF" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Inclui o Maturador de Chip</p>
                  <p className="text-xs text-gray-400">Usuários neste plano têm acesso ao maturador sem comprar o add-on de R$ 49,90</p>
                </div>
              </label>
            </div>

            {/* Telas liberadas */}
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Telas liberadas</label>
              <p className="text-xs text-gray-400 mb-3">Selecione quais telas os usuários deste plano terão acesso</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {TELAS_DISPONIVEIS.map(tela => {
                  const checked = form.telas.includes(tela.path);
                  return (
                    <label key={tela.path} className="flex items-center gap-3 cursor-pointer group">
                      <div
                        onClick={() => toggleTela(tela.path)}
                        className={`w-5 h-5 rounded-md border-2 flex-shrink-0 flex items-center justify-center transition-all ${
                          checked ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 group-hover:border-indigo-400'
                        }`}
                      >
                        {checked && (
                          <svg className="w-3 h-3 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm text-gray-700">{tela.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Ações */}
          <div className="flex items-center gap-3 mt-7 pt-6 border-t border-gray-100">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold rounded-xl transition-colors"
            >
              {saving ? (
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {saving ? 'Salvando...' : 'Salvar plano'}
            </button>
            <button
              onClick={cancelEdit}
              className="px-5 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* ── Lista de planos ─────────────────────────────────────────── */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-7 h-7 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : planos.length === 0 ? (
        <div className="border border-dashed rounded-2xl p-12 text-center bg-white border border-gray-200">
          <p className="text-gray-400 text-sm mb-3">Nenhum plano cadastrado ainda</p>
          <button onClick={openNew} className="text-sm text-[#A5B4FC] font-semibold hover:text-[#A5B4FC] transition-colors">
            + Criar primeiro plano
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {planos.map(p => (
            <div
              key={p.id}
              className={`border rounded-2xl p-5 transition-all ${ p.ativo ? '' : ' opacity-60' } bg-white border border-gray-200`}
            >
              <div className="flex items-start gap-4">
                {/* Info principal */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    <span className="text-base font-bold text-gray-900">{p.nome}</span>
                    {p.destaque && (
                      <span className="text-[11px] font-bold bg-[rgba(99,102,241,0.2)] text-[#A5B4FC] px-2.5 py-0.5 rounded-full">
                        ★ Mais popular
                      </span>
                    )}
                    {!p.ativo && (
                      <span className="text-[11px] font-bold bg-gray-100 text-gray-400 px-2.5 py-0.5 rounded-full">
                        Inativo
                      </span>
                    )}
                    <span className="text-xs text-gray-400 ml-auto">Ordem: {p.ordem}</span>
                  </div>

                  {/* Preço + stats */}
                  <div className="flex flex-wrap items-center gap-4 mb-3">
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-black text-gray-900">
                        R$ {Number(p.valor).toFixed(2).replace('.', ',')}
                      </span>
                      <span className="text-xs text-gray-400">/mês</span>
                    </div>
                    <div className="h-5 w-px bg-gray-200" />
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span className="font-semibold text-gray-700">{p.maxConexoes}</span> conexões
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      <span className="font-semibold text-gray-700">{p.maxDisparos === 0 ? 'Ilimitados' : p.maxDisparos}</span>
                      {p.maxDisparos > 0 && ' disparos/dia'}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                      <span className="font-semibold text-gray-700">{p.maxSessoesAtivas}</span> sessões simultâneas
                    </div>
                  </div>

                  {/* Especificações */}
                  {p.especificacoes.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {p.especificacoes.map((e, i) => (
                        <span key={i} className="text-xs bg-gray-100 text-gray-500 px-2.5 py-1 rounded-lg">
                          {e}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Ações */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => openEdit(p)}
                    title="Editar"
                    className="p-2 rounded-xl text-gray-400 hover:text-[#A5B4FC] hover:bg-[rgba(99,102,241,0.15)] transition-all"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleToggle(p)}
                    title={p.ativo ? 'Desativar' : 'Ativar'}
                    className={`p-2 rounded-xl transition-all ${p.ativo ? 'text-gray-400 hover:text-amber-500 hover:bg-yellow-50' : 'text-emerald-500 hover:bg-emerald-50'}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={p.ativo
                        ? "M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                        : "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"} />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(p.id)}
                    title="Excluir"
                    className="p-2 rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
